// "Invitations for you" (NoCompanyView.invitationsCard): pending invitations for this account's confirmed email, each
// joined only with an explicit click and a confirmation, plus "Check again".

import { MailPlus, RefreshCw, Users } from 'lucide-react';
import { useId } from 'react';
import type { InvitationOffer } from '@/contracts/wire';
import { Button, GT_COLORS, IconBadge, SectionCard } from '@/ui';
import { cn } from '@/ui/cn';
import { offerCompany } from '../offers';
import { invitationsEmptyDetail, invitationsEmptyTitle, JOIN_HINT, stillNothingDetail } from '../welcomeText';
import { ErrorNote, Footnote } from './Bits';
import { OfferSummary } from './OfferSummary';

export interface InvitationsSectionProps {
  offers: readonly InvitationOffer[];
  /** No answer yet from the first check. */
  loading: boolean;
  /** A check is running ("Check again", or a refresh in the background). */
  checking: boolean;
  /** "Check again" has run at least once (then "Still nothing" may show). */
  checkedOnce: boolean;
  /** A failed join, or why the last check couldn't reach the server. */
  error: string | null;
  email: string | null;
  /** The invitation being joined (its button spins). */
  joiningId: string | null;
  /** Something else is running: buttons are off. */
  disabled: boolean;
  onJoin: (offer: InvitationOffer) => void;
  onCheckAgain: () => void;
}

export function InvitationsSection({
  offers,
  loading,
  checking,
  checkedOnce,
  error,
  email,
  joiningId,
  disabled,
  onJoin,
  onCheckAgain,
}: InvitationsSectionProps) {
  const hintId = useId();
  const empty = offers.length === 0;
  return (
    <SectionCard title="Invitations for you" bodyClassName="flex flex-col gap-4">
      {empty ? (
        <div className="flex items-start gap-3" aria-live="polite">
          <IconBadge icon={<MailPlus />} color={GT_COLORS.orange} size={44} />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-fg">{invitationsEmptyTitle(loading)}</p>
            <p className="mt-0.5 text-sm text-fg-muted">{invitationsEmptyDetail(email)}</p>
          </div>
        </div>
      ) : (
        <ul className="flex flex-col gap-4" aria-label="Invitations">
          {offers.map((offer, index) => (
            <li key={offer.id} className={cn('flex flex-col gap-3', index > 0 && 'border-t border-line pt-4')}>
              <OfferSummary offer={offer} />
              <Button
                variant="primary"
                size="lg"
                fullWidth
                icon={<Users />}
                loading={joiningId === offer.id}
                disabled={disabled}
                aria-describedby={hintId}
                onClick={() => onJoin(offer)}
              >
                {joiningId === offer.id ? 'Joining…' : `Join ${offerCompany(offer)}`}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <span id={hintId} className="sr-only">
        {JOIN_HINT}
      </span>
      {error && <ErrorNote>{error}</ErrorNote>}
      <Button
        variant="secondary"
        size="lg"
        fullWidth
        icon={<RefreshCw />}
        loading={checking && !loading}
        disabled={checking || disabled}
        onClick={onCheckAgain}
      >
        {checking && !loading ? 'Checking…' : 'Check again'}
      </Button>
      {checkedOnce && !checking && empty && !error && <Footnote>{stillNothingDetail(email)}</Footnote>}
    </SectionCard>
  );
}
